import { authFetch } from './auth-fetch';
import type { Page } from './endpoints';

/**
 * The six calls of `12 §10` — the conversation viewer of `06 §7`.
 *
 * Same rule as every other module here: no client-side modelling and no
 * renaming. In particular `blocked` and `decision` come off the wire exactly as
 * the backend computed them, because the whole point of that pair is that the
 * screen shows what the engine decided rather than its own reconstruction.
 */

export type ConversationScope = 'internal' | 'inter_company';
export type ConversationStatus = 'open' | 'resolved' | 'archived';
export type MessageType = 'text' | 'request' | 'response' | 'delegation' | 'notification' | 'escalation';
export type MessagePriority = 'low' | 'normal' | 'high' | 'urgent';

export interface Conversation {
  id: string;
  topic: string;
  scope: ConversationScope;
  status: ConversationStatus;
  task_id: string | null;
  task_number: number | null;
  agreement_id: string | null;
  message_count: number;
  last_message_at: string | null;
  created_at: string;
}

export interface ConversationParticipant {
  employee_id: string;
  slug: string;
  name: string;
  job_title: string;
  joined_at: string;
}

export interface ConversationDetail extends Conversation {
  participants: ConversationParticipant[];
}

/** The decision that refused a message — present only on blocked attempts. */
export interface MessageDecision {
  id: string;
  target_descriptor: string;
  reason_code: string;
  reason_detail: string;
  matched_policy_id: string | null;
}

export interface Message {
  id: string;
  conversation_id: string;
  from: { employee_id: string; slug: string; name: string; job_title: string };
  recipients: string[];
  type: MessageType;
  subject: string | null;
  body: string;
  payload: Record<string, unknown> | null;
  status: string;
  blocked: boolean;
  priority: MessagePriority;
  requires_response: boolean;
  response_deadline_at: string | null;
  in_reply_to_message_id: string | null;
  task_run_id: string | null;
  redacted: boolean;
  created_at: string;
  decision: MessageDecision | null;
}

export interface BlockedAttempt {
  id: string;
  message_id: string | null;
  actor_slug: string;
  actor_name: string;
  target_descriptor: string;
  reason_code: string;
  reason_detail: string;
  matched_policy_id: string | null;
  created_at: string;
}

export interface InboxItem {
  id: string;
  message_id: string;
  conversation_id: string;
  from: string;
  from_name: string;
  from_job_title: string;
  subject: string | null;
  body: string;
  type: MessageType;
  priority: MessagePriority;
  status: string;
  requires_response: boolean;
  response_deadline_at: string | null;
  wakes_employee: boolean;
  read_at: string | null;
  received_at: string;
}

export const conversations = {
  list: (companyId: string, filter: Record<string, string> = {}) => {
    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(filter)) {
      if (value !== '') params.append(`filter[${key}]`, value);
    }

    const query = params.toString();

    return authFetch<Page<Conversation>>(`/companies/${companyId}/conversations${query === '' ? '' : `?${query}`}`);
  },
  show: (id: string) => authFetch<ConversationDetail>(`/conversations/${id}`),
  messages: (id: string, limit = 100) =>
    authFetch<Page<Message>>(`/conversations/${id}/messages?limit=${String(limit)}`),
  archive: (id: string) => authFetch<ConversationDetail>(`/conversations/${id}/archive`, { method: 'POST' }),
  blockedAttempts: (companyId: string) =>
    authFetch<Page<BlockedAttempt>>(`/companies/${companyId}/blocked-attempts`),
  inbox: (employeeId: string) =>
    authFetch<Page<InboxItem> & { unread_count: number }>(`/employees/${employeeId}/inbox`),
};

/** `/companies/{c}/conversations/{id}` — the live thread topic of `06 §7`. */
export function conversationTopic(companyId: string, conversationId: string): string {
  return `/companies/${companyId}/conversations/${conversationId}`;
}

export function conversationsTopic(companyId: string): string {
  return `/companies/${companyId}/conversations`;
}
